import type { AccessorColumnDef, ColumnMeta, FilterFnOption, PaginationState, Row } from '@tanstack/react-table';
import { DateTime } from '@ksyos/date-time';
import {
    Box,
    Icon,
    Link,
    Pagination,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    Tooltip,
} from '@ksyos/design-system';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import type {
    DuplicateOrganizations,
    OrganizationDataError,
    OrganizationDataErrorWithLastLogin,
} from '@common/interfaces/api/organizationDataErrorsApi';
import { OrganizationKgoStatus } from '@common/enums';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { FilterRow } from '@frontend/components/elements/tanStackFilter/FilterRow';
import { dateAfterFilter } from '@frontend/components/elements/tanStackFilter/utils/dateAfterFilter';
import { getSortDirection } from '@frontend/components/elements/tanStackFilter/utils/getSortDirection';
import { getSelectedLanguage } from '@frontend/translate';
import { usePersistentTableState } from '@frontend/utils/hooks/usePersistedTableState';
import ErrorDetailsCell from './ErrorDetailsCell';
import ErrorTypeCell from './ErrorTypeCell';
import KgoStatusCell from './KgoStatusCell';
import OrganizationIdCell from './OrganizationIdCell';
import OrganizationTableRow from './OrganizationTableRow';

const columnHelper = createColumnHelper<OrganizationDataErrorWithLastLogin>();

type OrganizationChecksProps = { items: OrganizationDataErrorWithLastLogin[] };

export default function OrganizationChecksTable({ items }: Readonly<OrganizationChecksProps>) {
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });
    const translate = getSelectedLanguage();

    const columns: (
        | AccessorColumnDef<OrganizationDataError, string>
        | AccessorColumnDef<OrganizationDataError, DuplicateOrganizations | null>
        | AccessorColumnDef<OrganizationDataError, string & DuplicateOrganizations & Date>
    )[] = [
        columnHelper.accessor('organization.txcsId', {
            cell: ({ row }) => <OrganizationIdCell row={row} />,
            enableSorting: true,
            header: translate.dataChecks.id,
        }),
        columnHelper.accessor('organization.name', {
            enableSorting: true,
            header: translate.dataChecks.name,
        }),
        createCustomColumn((row) => `${translate.organizationTypeEnum[row.organization.organizationType]}`, {
            cell: ({ row }) => <>{translate.organizationTypeEnum[row.original.organization.organizationType]}</>,
            header: translate.dataChecks.organizationType,
        }),
        columnHelper.accessor('organizationLastLoginOn', {
            cell: ({ row }) => <NullableFormattedDate value={row.original.organizationLastLoginOn} />,
            enableSorting: true,
            filterFn: dateAfterFilter,
            header: () => (
                <>
                    {translate.dataChecks.lastLogin}
                    <Tooltip content={translate.dataChecks.lastLoginTooltip}>
                        <Icon
                            icon="information"
                            sx={{ ml: 2 }}
                        />
                    </Tooltip>
                </>
            ),
            meta: {
                filterVariant: 'dateAfter',
            },
            sortingFn: (a, b) =>
                DateTime.from(a.original.organizationLastLoginOn ?? 0).diff(b.original.organizationLastLoginOn ?? 0),
        }),
        createCustomColumn((row) => `${translate.organizationDataErrorType[row.type]}`, {
            cell: ({ row }) => <ErrorTypeCell row={row} />,
            header: () => (
                <>
                    {translate.dataChecks.checkType}
                    <Tooltip content={translate.dataChecks.typeTooltip}>
                        <Icon
                            icon="information"
                            sx={{ ml: 2 }}
                        />
                    </Tooltip>
                </>
            ),
            id: translate.dataChecks.checkType,
        }),
        createCustomColumn(
            (row) =>
                `${row.organization.kgoStatus ? translate.organization.kgoStatuses[row.organization.kgoStatus] : ''}`,
            {
                cell: ({ row }) => <KgoStatusCell row={row} />,
                header: translate.organization.kgoStatus,
                id: translate.organization.kgoStatus,
            }
        ),
        columnHelper.accessor('created_at', {
            cell: ({ row }) => <NullableFormattedDate value={row.original.created_at} />,
            enableSorting: true,
            filterFn: dateAfterFilter,
            header: translate.dataChecks.checkDate,
            meta: {
                filterVariant: 'dateAfter',
            },
        }),
        columnHelper.accessor('details', {
            cell: ({ row }) => <ErrorDetailsCell dataError={row.original} />,
            enableColumnFilter: false,
            enableSorting: false,
            header: translate.dataChecks.checkDetails,
        }),
    ];

    const defaultSorting = useMemo(() => [{ desc: true, id: 'created_at' }], []);
    const defaultFilter = useMemo(
        () => [
            {
                id: translate.organization.kgoStatus,
                value: [
                    translate.organization.kgoStatuses[OrganizationKgoStatus.attentionRequired],
                    translate.organization.kgoStatuses[OrganizationKgoStatus.inProgress],
                ],
            },
        ],
        []
    );

    const { columnFilters, setColumnFilters, sorting, setSorting } = usePersistentTableState({
        defaultFilter,
        defaultSorting,
        storageKeyPrefix: 'organizationChecksTable',
    });

    const { setPageIndex, setPageSize, getHeaderGroups, getRowModel, getPrePaginationRowModel } = useReactTable({
        columns,
        data: items,
        getCoreRowModel: getCoreRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        state: { columnFilters, pagination, sorting },
    });

    return (
        <>
            <Box
                display="flex"
                justifyContent="flex-end"
                margin={4}
            >
                <Link
                    onClick={() => setColumnFilters([])}
                    variant="button"
                >
                    {translate.common.clearFilters}
                </Link>
            </Box>
            <TableContainer>
                <Table
                    spacing="medium"
                    style={{ borderCollapse: 'collapse' }}
                >
                    <TableHead>
                        {getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHeader
                                        isSortable={header.column.getCanSort()}
                                        isSortHeader={!!header.column.getIsSorted()}
                                        key={header.id}
                                        onClick={header.column.getToggleSortingHandler()}
                                        sortDirection={getSortDirection(header)}
                                        style={{ padding: 0 }}
                                    >
                                        {!header.isPlaceholder &&
                                            flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHeader>
                                ))}
                            </TableRow>
                        ))}
                    </TableHead>
                    <TableBody>
                        <FilterRow headerGroups={getHeaderGroups()} />
                        {getRowModel().rows.map((row) => (
                            <OrganizationTableRow
                                key={row.id}
                                row={row}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {getPrePaginationRowModel().rows.length > pagination.pageSize && (
                <Pagination
                    onChange={({ page: newPage, pageSize: newPageSize }) => {
                        setPageIndex(newPage - 1);
                        setPageSize(newPageSize);
                    }}
                    page={pagination.pageIndex + 1}
                    pageSize={pagination.pageSize}
                    totalItems={getPrePaginationRowModel().rows.length}
                />
            )}
        </>
    );

    function createCustomColumn<T>(
        accessorFn: (row: OrganizationDataError) => T,
        options: {
            cell: (props: { row: Row<OrganizationDataError> }) => JSX.Element;
            enableSorting?: boolean;
            filterFn?: FilterFnOption<OrganizationDataError>;
            header: string | (() => JSX.Element);
            id?: string;
            meta?: ColumnMeta<OrganizationDataError, T>;
        }
    ): AccessorColumnDef<OrganizationDataError, T> {
        const id =
            options.id ??
            (typeof options.header === 'string' ? options.header : `column_${Math.random().toString(36).slice(2)}`);

        return {
            accessorFn,
            cell: options.cell,
            enableSorting: options.enableSorting ?? true,
            filterFn: options.filterFn ?? 'arrIncludesSome',
            header: options.header,
            id,
            meta: options.meta ?? { filterVariant: 'select' },
        };
    }
}
